import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S4Query, S4Record, S4AssetType, S4AssetStatus } from "types";
import { formatCurrency, formatDateSafe } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S4Query = {
  from_date: startOfYear,
  to_date: todayString,
};

// Asset type labels for UI
const ASSET_TYPE_LABELS: Record<S4AssetType, string> = {
  CASH: "Tiền mặt",
  BANK: "Tiền gửi ngân hàng",
  TOOL: "Công cụ, dụng cụ",
  FIXED_ASSET: "Tài sản cố định",
};

// Status labels for UI
const STATUS_LABELS: Record<S4AssetStatus, string> = {
  IN_USE: "Đang dùng",
  DISPOSED: "Thanh lý",
};

const S4Report: React.FC = () => {
  const { fetchS4Hkd } = useReport();
  const [filters, setFilters] = useState<S4Query>(defaultFilters);
  const [submitted, setSubmitted] = useState<S4Query | null>(defaultFilters);

  useEffect(() => {
    fetchS4Hkd.mutate(defaultFilters);
  }, [fetchS4Hkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S4Query, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS4Hkd.mutate(filters);
    },
    [fetchS4Hkd, filters],
  );

  // Get data from API response
  const responseData = fetchS4Hkd.data?.data;
  const filteredRecords = (responseData?.records ?? []) as S4Record[];
  const apiSummary = responseData?.summary;

  const summary = useMemo(() => {
    if (apiSummary) {
      return apiSummary;
    }
    // Fallback to calculating from records
    const total_original_cost = filteredRecords.reduce(
      (sum, r) => sum + (r.original_cost || 0),
      0,
    );
    const total_remaining_value = filteredRecords.reduce(
      (sum, r) => sum + (r.remaining_value || 0),
      0,
    );

    const cash_value = filteredRecords
      .filter((r) => r.asset_type === "CASH")
      .reduce((sum, r) => sum + (r.remaining_value || 0), 0);

    const bank_value = filteredRecords
      .filter((r) => r.asset_type === "BANK")
      .reduce((sum, r) => sum + (r.remaining_value || 0), 0);

    const tool_value = filteredRecords
      .filter((r) => r.asset_type === "TOOL")
      .reduce((sum, r) => sum + (r.remaining_value || 0), 0);

    const fixed_asset_value = filteredRecords
      .filter((r) => r.asset_type === "FIXED_ASSET")
      .reduce((sum, r) => sum + (r.remaining_value || 0), 0);

    return {
      total_original_cost,
      total_remaining_value,
      cash_value,
      bank_value,
      tool_value,
      fixed_asset_value,
    };
  }, [apiSummary, filteredRecords]);

  const getStatusBadgeClass = (status: S4AssetStatus) => {
    switch (status) {
      case "IN_USE":
        return "bg-green-100 text-green-800 border-green-200";
      case "DISPOSED":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const columns = useMemo(
    () => [
      {
        headerName: "Tên tài sản",
        field: "asset_name",
        pinned: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Ngày ghi nhận",
        field: "accounting_date",
        pinned: "left",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Loại tài sản",
        field: "asset_type",
        minWidth: 160,
        valueFormatter: (params: any) =>
          ASSET_TYPE_LABELS[params.value as S4AssetType] || params.value,
      },
      {
        headerName: "Nguyên giá",
        field: "original_cost",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-medium text-gray-700",
      },
      {
        headerName: "Giá trị còn lại",
        field: "remaining_value",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
      {
        headerName: "Tình trạng",
        field: "status",
        minWidth: 120,
        cellRenderer: (params: any) => {
          const status = params.value as S4AssetStatus;
          const badgeClass = getStatusBadgeClass(status);
          return `<span class="px-2 py-1 text-xs font-semibold rounded border ${badgeClass}">${STATUS_LABELS[status]}</span>`;
        },
      },
      {
        headerName: "Ghi chú",
        field: "note",
        minWidth: 180,
        valueFormatter: (params: any) => params.value || "",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS4Hkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}
      <div>
        <p className="text-sm text-gray-500 uppercase">Báo cáo S4-HKD</p>
        <h1 className="text-2xl font-semibold text-gray-900">
          Sổ theo dõi tài sản, nguồn vốn
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Theo dõi tài sản phục vụ sản xuất kinh doanh
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày *
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày *
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              📊 Tổng hợp tài sản
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-600">Tổng nguyên giá</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(summary.total_original_cost)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tổng giá trị còn lại</p>
                <p className="text-xl font-bold text-green-700">
                  {formatCurrency(summary.total_remaining_value)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Khấu hao tích lũy</p>
                <p className="text-xl font-bold text-orange-700">
                  {formatCurrency(
                    summary.total_original_cost - summary.total_remaining_value,
                  )}{" "}
                  ₫
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-blue-200">
              <div>
                <p className="text-xs text-gray-600">Tiền mặt</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(summary.cash_value)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tiền gửi ngân hàng</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(summary.bank_value)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Công cụ, dụng cụ</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(summary.tool_value)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Tài sản cố định</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(summary.fixed_asset_value)}
                </p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-blue-800">
                💡 Tài sản có khấu hao sẽ sinh chi phí trong S3
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <p className="text-sm text-gray-500">
              {filteredRecords.length} bản ghi
            </p>
          </div>

          <AgGridTable
            rows={filteredRecords}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S4Report;
